import { useEffect, useRef, useState } from "react";
import { PrismButton } from "@prism-ui/react";
import SubmittedApp from "../SubmittedApp";
import {
  Confetti,
  MAX_COMMENT_WORDS,
  RankChips,
  TRACK_META,
  countWords,
  day,
  singular,
} from "./raterParts";

const typingIn = (el) =>
  !!el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

/*
 * Full-screen reader. "queue" walks the unread pile (Save & next, Skip for now); "review" walks
 * the rater's existing ratings in the same order so any of them can be changed until rating closes.
 */
const RaterReader = ({
  mode,
  track,
  startId,
  rows,
  mask,
  chapterName,
  open,
  lateUntil,
  currentUser,
  onSave,
  onSkip,
  onReview,
  onClose,
}) => {
  const meta = TRACK_META[track.typeId];
  const review = mode === "review";
  const [queue, setQueue] = useState(() => [...track.pending]);
  const [list] = useState(() => [...track.rated]);
  const [index, setIndex] = useState(() => Math.max(0, track.rated.indexOf(startId)));
  const [finished, setFinished] = useState(false);
  const currentId = finished ? null : review ? list[index] : queue[0];
  const row = currentId != null ? rows.get(currentId) : null;

  const [rank, setRank] = useState(null);
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState("");
  const dialogRef = useRef(null);
  const noteRef = useRef(null);
  const scrollRef = useRef(null);
  const flashTimer = useRef(null);

  // Reset the dock whenever a different application comes up, never on a parent re-render.
  useEffect(() => {
    const r = currentId != null ? rows.get(currentId) : null;
    setRank(r?.rating ?? null);
    setNote(r?.comment || "");
    setNoteOpen(!!r?.comment);
    scrollRef.current?.scrollTo?.({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      clearTimeout(flashTimer.current);
    };
  }, []);

  const words = countWords(note);
  const over = words > MAX_COMMENT_WORDS;
  const changed = !!row && (rank !== row.rating || note.trim() !== (row.comment || "").trim());
  const left = queue.length;
  const ratedCount = track.total - left;

  const say = (msg) => {
    setFlash(msg);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(""), 1800);
  };

  const advanceReview = () => {
    if (index + 1 >= list.length) setFinished(true);
    else setIndex(index + 1);
  };

  const save = async () => {
    if (saving || !row) return;
    if (review && (!open || !changed)) return advanceReview();
    if (!open || rank === null || over) return;
    setSaving(true);
    const ok = await onSave(row, rank, note);
    setSaving(false);
    if (!ok) return;
    if (review) {
      say("Saved");
      return advanceReview();
    }
    const remaining = queue.slice(1);
    setQueue(remaining);
    if (remaining.length === 0) setFinished(true);
    else say(`Saved · ${remaining.length} left`);
  };

  const skip = () => {
    if (review || saving || !row) return;
    if (queue.length < 2) return say("This is the last one left");
    setQueue([...queue.slice(1), queue[0]]);
    onSkip(row.application_id);
    say("Skipped · it'll come back at the end");
  };

  const back = () => {
    if (finished) {
      setFinished(false);
      setIndex(list.length - 1);
    } else if (index > 0) setIndex(index - 1);
  };

  const openNote = () => {
    setNoteOpen(true);
    setTimeout(() => noteRef.current?.focus(), 0);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (typingIn(e.target)) {
        if (e.key === "Escape") e.target.blur();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape") return onClose();
      if (finished) {
        if (e.key === "Enter") onClose();
        return;
      }
      if (open && /^[1-6]$/.test(e.key)) setRank(Number(e.key) - 1);
      else if (e.key.toLowerCase() === "n" && open) {
        e.preventDefault();
        openNote();
      } else if (e.key.toLowerCase() === "s" && !review) skip();
      else if (e.key === "Enter") save();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const reviewDone = review && finished;
  const caughtUp = !review && finished;
  const heading = caughtUp ? "All caught up" : reviewDone ? "Review complete" : row?.label || "Loading…";
  const blindLabel = mask?.name ? row?.label : undefined;

  let saveLabel = "Save & next";
  if (review) {
    const last = index + 1 >= list.length;
    if (!open) saveLabel = last ? "Finish" : "Next";
    else if (changed) saveLabel = last ? "Save & finish" : "Save & next";
    else saveLabel = last ? "Finish" : "Next";
  }

  return (
    <div className="rr-reader-backdrop">
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`rr-reader rr-accent-${meta.accent}`}
        role="dialog"
        aria-modal="true"
        aria-label={caughtUp || reviewDone ? heading : `Reading ${heading}`}
      >
        <header className="rr-reader-top">
          <span className="rr-pill">
            <i aria-hidden="true">📍</i> {chapterName ? `${chapterName} · ` : ""}
            {singular(track.typeId)}
          </span>
          <h2>{heading}</h2>
          {review && <span className="rr-review-tag">Reviewing my ratings</span>}
          <div
            className="rr-reader-progress"
            aria-label={review ? `Rating ${Math.min(index + 1, list.length)} of ${list.length}` : `${ratedCount} of ${track.total} rated`}
          >
            {review ? (
              <>
                <span>
                  <b>{Math.min(index + 1, list.length)}</b> of {list.length}
                </span>
                <div className="rr-bar">
                  <i style={{ width: `${((finished ? list.length : index + 1) / list.length) * 100}%` }} />
                </div>
              </>
            ) : (
              <>
                <span>
                  <b>{left}</b> left
                </span>
                <div className="rr-bar">
                  <i style={{ width: `${track.total ? (ratedCount / track.total) * 100 : 100}%` }} />
                </div>
                <small>
                  {ratedCount} of {track.total}
                </small>
              </>
            )}
          </div>
          <button type="button" className="rr-close" aria-label="Close reader" onClick={onClose}>
            ✕
          </button>
        </header>

        {caughtUp ? (
          <div className="rr-celebrate">
            <Confetti />
            <div className="rr-celebrate-card">
              <span className="rr-celebrate-emoji" aria-hidden="true">🎉</span>
              <h2>You&apos;re caught up on {meta.label}!</h2>
              <p>
                Every {singular(track.typeId).toLowerCase()} application so far has your rating.
                {lateUntil ? (
                  <>
                    {" "}
                    Late applications can still arrive until <b>{day(lateUntil)}</b>, so check back.
                  </>
                ) : null}{" "}
                You can still go back through any of your ratings until rating closes.
              </p>
              <div className="rr-track-actions" style={{ justifyContent: "center" }}>
                <PrismButton onPrismClick={onClose}>
                  <span>Back to my ratings</span>
                </PrismButton>
                <PrismButton tone="secondary" display="outline" onPrismClick={() => onReview(track.typeId)}>
                  <span>Review them now</span>
                </PrismButton>
              </div>
            </div>
          </div>
        ) : reviewDone ? (
          <div className="rr-review-done">
            <div className="rr-celebrate-card">
              <span className="rr-celebrate-emoji" aria-hidden="true">✅</span>
              <h2>That&apos;s all {list.length} of your {meta.label.toLowerCase()} ratings</h2>
              <p>{open ? "Anything you changed is saved. You can come back and revise until rating closes." : "Rating is closed, so these are final."}</p>
              <div className="rr-track-actions" style={{ justifyContent: "center" }}>
                <PrismButton onPrismClick={onClose}>
                  <span>Back to my ratings</span>
                </PrismButton>
                <PrismButton tone="secondary" display="ghost" onPrismClick={back}>
                  <span>Previous</span>
                </PrismButton>
              </div>
            </div>
          </div>
        ) : (
          <div className={`rr-reader-body${noteOpen ? " with-note" : ""}`}>
            <div className="rr-reader-scroll" ref={scrollRef}>
              {row && (
                <div className="rr-slide rr-reader-app" key={row.application_id}>
                  <SubmittedApp
                    key={row.application_id}
                    params={{ application_id: row.application_id }}
                    currentUser={currentUser}
                    blindMask={mask}
                    blindLabel={blindLabel}
                    hideHeader
                  />
                </div>
              )}
            </div>

            {noteOpen && (
              <aside className="rr-note" aria-label="Your note">
                <header>
                  <h3>Your note</h3>
                  <button type="button" className="rr-note-hide" onClick={() => setNoteOpen(false)}>
                    Hide
                  </button>
                </header>
                <p className="rr-note-help">Optional. Your chapter officers can read it in Host Review.</p>
                <textarea
                  ref={noteRef}
                  aria-label="Note"
                  value={note}
                  disabled={!open}
                  placeholder="What stood out? Write while you read…"
                  onChange={(e) => setNote(e.target.value)}
                />
                <footer className={over ? "over" : ""}>
                  {words}/{MAX_COMMENT_WORDS} words · saved with your rating
                </footer>
              </aside>
            )}
          </div>
        )}

        {!finished && (
          <footer className="rr-dock">
            <RankChips value={rank} onPick={setRank} disabled={!open || saving} />
            <div className="rr-dock-actions">
              {!open && <span className="rr-dock-hint">Rating is closed · read only</span>}
              {!noteOpen && open && (
                <button type="button" className="rr-link" onClick={openNote}>
                  ✎ {note ? "Edit note" : "Add a note"} <kbd>N</kbd>
                </button>
              )}
              {review ? (
                <PrismButton tone="secondary" display="ghost" disabled={index === 0} onPrismClick={back}>
                  <span>Previous</span>
                </PrismButton>
              ) : (
                <PrismButton tone="secondary" display="ghost" disabled={saving} onPrismClick={skip}>
                  <span>Skip for now</span>
                </PrismButton>
              )}
              <PrismButton
                disabled={saving || (!review && (rank === null || !open)) || (open && changed && (rank === null || over))}
                onPrismClick={save}
              >
                <span>{saving ? "Saving…" : saveLabel}</span>
              </PrismButton>
            </div>
          </footer>
        )}
        {flash && (
          <div className="rr-toast" role="status">
            {flash}
          </div>
        )}
      </div>
    </div>
  );
};

export default RaterReader;
